// Memoized table rows for ShopFloorTerminalV2.
//
// WHY THIS FILE EXISTS (performance): the terminal can show up to 500 rows.
// When these were inline render functions inside the terminal component,
// every state change — expanding ONE row, opening a dialog, the 30s
// auto-refresh tick — re-rendered all 500 heavy MUI rows, which is what made
// expansion feel laggy. `React.memo` + stable callbacks mean a row only
// re-renders when ITS props change (e.g. its own isExpanded flips).
//
// Both components receive ONLY stable or primitive props:
//   - step / group objects come from useMemo'd arrays (stable between
//     expansion toggles; refreshes legitimately replace them)
//   - booleans (isExpanded, materialAware, isStaff, groupByDate)
//   - useCallback'd handlers from the terminal

import React from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Grid, IconButton, LinearProgress,
  Paper, Stack, TableCell, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  Delete, DeleteSweep, Edit, ExpandLess, ExpandMore, Error as ErrorIcon, Info,
  Link as LinkIcon, Lock, PlayArrow, ShoppingCart,
} from '@mui/icons-material';

import DemandTimelineCellV2 from './DemandTimelineCellV2';
import DemandTimelineDisplayV2 from './DemandTimelineDisplayV2';
import LinkedOrdersDisplayV2 from './LinkedOrdersDisplayV2';
import GroupMaterialAwareStartButtonV2 from './GroupMaterialAwareStartButtonV2';
import MaterialAwareStartButtonV2 from './MaterialAwareStartButtonV2';
import MaterialRequirementsTableV2 from './MaterialRequirementsTableV2';
import MaterialStatusChipV2 from './MaterialStatusChipV2';
import OrderFactoryNotesV2 from './OrderFactoryNotesV2';
import RootItemsDisplayV2 from './RootItemsDisplayV2';
import { highlightsForItem, applyHighlight } from '../utils/highlightText';
import { orderStatusColor, orderStatusLabel } from '../utils/orderStatus';

// ---------------------------------------------------------------------------
// Pure helpers (mirrors v1's date / urgency logic exactly). Exported so the
// terminal's sort logic can share them.
// ---------------------------------------------------------------------------

export function getUrgencyLevel(step) {
  if (!step?.due_date) return 'NORMAL';
  const now = new Date();
  const due = new Date(step.due_date);
  const days = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'OVERDUE';
  if (days <= 1) return 'URGENT';
  if (days <= 7) return 'HIGH';
  return 'NORMAL';
}

export function getPriorityColor(urgency) {
  switch (urgency) {
    case 'OVERDUE': return 'error';
    case 'URGENT': return 'error';
    case 'HIGH': return 'warning';
    default: return 'default';
  }
}

export function progressPercentage(step) {
  const total = parseFloat(step?.quantity_to_produce) || 0;
  const done = parseFloat(step?.quantity_completed) || 0;
  if (total <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

// Multi-step badge (mirrors v1 StepIndicator)
export function StepIndicator({ step }) {
  if (!step || !step.total_steps || step.total_steps <= 1) return null;
  const color = step.sequence === 1 ? 'primary'
    : step.sequence === step.total_steps ? 'success' : 'info';
  return (
    <Tooltip title={`Routing step ${step.sequence} of ${step.total_steps}`} arrow>
      <Chip
        icon={<LinkIcon />}
        label={`Step ${step.sequence}/${step.total_steps}`}
        size="small"
        color={color}
        variant="outlined"
        sx={{ fontWeight: 'bold', ml: 1, '& .MuiChip-icon': { fontSize: '0.9rem' } }}
      />
    </Tooltip>
  );
}

// Dependency-blocker alert
export function DependencyBlockerAlert({ step }) {
  if (!step || step.total_steps <= 1 || !step.depends_on_id) return null;
  if (!step.can_start) {
    return (
      <Alert severity="warning" icon={<Lock />} sx={{ mt: 1, py: 0.5 }}>
        <Typography variant="caption">
          <strong>Pending:</strong> Step {(step.sequence || 1) - 1} hasn't started yet —
          you can still override
        </Typography>
      </Alert>
    );
  }
  if (
    step.depends_on_status !== 'completed'
    && parseFloat(step.qty_available_from_previous_step || 0) > 0
  ) {
    return (
      <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
        <Typography variant="caption">
          <strong>Partial:</strong> {parseFloat(step.qty_available_from_previous_step)} units
          released from Step {(step.sequence || 1) - 1}
        </Typography>
      </Alert>
    );
  }
  return null;
}

// Former per-cell render-skipping wrapper — now intentionally EMPTY.
//
// This used to carry `content-visibility: auto` + `contain-intrinsic-size`
// on a block Box inside each cell (the <tr>/<td> themselves are excluded
// from size containment by the spec, so v1's row-level attempt was a silent
// no-op — which is exactly why v1 never flickered). Making it "work" per
// cell turned out to be the cause of the scroll judder/flicker on the
// individual view: inside a <table>, every skip/unskip at the viewport edge
// snaps a cell between its ~72px placeholder and its real (often much
// taller) height, each cell in a row resolves at a different moment, and
// ANY cell height change re-runs layout for the whole table — rows
// flickered randomly while scrolling and the page re-settled after
// stopping.
//
// Scroll performance is instead handled where the cost actually is —
// mounting: rows stream in idle-time chunks (RENDER_CHUNK) and the
// all-operations page caps automatic mounting (ALL_OPS_RENDER_CAP + "Show
// more"). A few hundred STATIC rows scroll smoothly without any skipping.
//
// Kept as an empty object because it is spread together with layout props
// (width/height/display) at the call sites.
const SKIP_CELL_SX = {};

function DueDateCell({ dueDate, moreDates = null }) {
  if (!dueDate) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }
  const days = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
  const primaryColor = days < 0 || days === 0 ? 'error.main' : days <= 2 ? 'warning.main' : 'text.primary';
  const captionColor = days < 0 || days === 0 ? 'error.main' : days <= 2 ? 'warning.main' : 'text.secondary';
  let captionText;
  if (days < 0) captionText = `${Math.abs(days)}d overdue`;
  else if (days === 0) captionText = 'Due today';
  else if (days === 1) captionText = 'Due tomorrow';
  else captionText = `${days}d remaining`;
  return (
    <Box>
      <Typography variant="body2" fontWeight="bold" color={primaryColor}>
        {new Date(dueDate).toLocaleDateString()}
      </Typography>
      <Typography variant="caption" color={captionColor}>
        {captionText}
      </Typography>
      {moreDates && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {moreDates}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Individual row (one step) — memoized
// ---------------------------------------------------------------------------

export const StepRowV2 = React.memo(function StepRowV2({
  s, isExpanded, materialAware, isStaff, showDemand, highlightCfg,
  onToggle, onStart, onAdminEdit, onDelete, onOrderSummary, onScrap,
}) {
  const urgency = getUrgencyLevel(s);
  const itemHighlights = highlightsForItem(
    highlightCfg, s.output_item_code, s.output_item_name,
  );
  const total = parseFloat(s.quantity_to_produce) || 0;
  const done = parseFloat(s.quantity_completed) || 0;
  const remaining = Math.max(0, total - done);
  const progress = progressPercentage(s);

  return (
    <>
      <TableRow
        data-testid={`step-row-${s.step_id}`}
        sx={{
          '&:hover': { backgroundColor: 'action.hover' },
          cursor: 'pointer',
          ...(s.total_steps > 1 && !s.can_start && {
            backgroundColor: 'grey.100',
            opacity: 0.8,
          }),
        }}
        onClick={() => onToggle(s.step_id)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" fontWeight="bold">
                  {s.wo_number}
                </Typography>
                <StepIndicator step={s} />
              </Box>
              <Chip
                label={urgency}
                color={getPriorityColor(urgency)}
                size="small"
                icon={urgency === 'OVERDUE' ? <ErrorIcon /> : undefined}
              />
              <DependencyBlockerAlert step={s} />
            </Box>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </TableCell>

        <TableCell>
          <Box sx={SKIP_CELL_SX}>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {applyHighlight(s.output_item_name, itemHighlights)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {applyHighlight(s.output_item_code, itemHighlights)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              via {s.operation_code}{s.operation_name ? ` — ${s.operation_name}` : ''}
            </Typography>
            <RootItemsDisplayV2 step={s} />
            <LinkedOrdersDisplayV2 step={s} />
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant="h5" color="error.main" fontWeight="bold">
            {remaining}
          </Typography>
          <Typography variant="caption">of {total} total</Typography>
        </TableCell>

        {showDemand && (
          <TableCell>
            {/* Total Demand column — backend precomputes per output_item.
                Optional (default hidden) via the Demand toggle. The inner
                Box fixes the width — maxWidth on a TableCell is ignored by
                auto table layout, which let this column crowd the Actions
                column off-screen. */}
            <Box sx={{ ...SKIP_CELL_SX, width: 150 }}>
              <DemandTimelineCellV2 timeline={s.demand_timeline} />
            </Box>
          </TableCell>
        )}

        <TableCell>
          {!materialAware
            ? <Chip label={s.operation_code || '-'} size="small" color="default" />
            : <MaterialStatusChipV2 step={s} />}
        </TableCell>

        <TableCell>
          {s.customer_order_number ? (
            <Box sx={SKIP_CELL_SX}>
              <Chip
                label={s.customer_order_number}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, fontSize: '0.85rem' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Line {s.line_number}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* height:1px lets the inner 100% chain resolve, so the notes box
            stretches to the row's full height (browser table-cell quirk). */}
        <TableCell sx={{ height: '1px' }}>
          {/* Order Notes column — the shared per-order note, editable right
              here in the table (autosaves; synced with every other copy). */}
          {s.customer_order_id != null ? (
            <Box
              sx={{ ...SKIP_CELL_SX, width: 200, height: '100%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <OrderFactoryNotesV2
                orderId={s.customer_order_id}
                initialNotes={s.customer_order_notes}
                compact
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        <TableCell>
          <DueDateCell dueDate={s.due_date} />
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 100, mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                color={progress >= 100 ? 'success' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold">{progress}%</Typography>
          </Box>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Scrap a part of the unit being built (remade on the spot —
                the WO is not changed). Native title= for the same per-row
                cost reason as the admin buttons below. */}
            {onScrap && (s.status === 'planned' || s.status === 'in_progress') && (
              <IconButton
                size="small"
                color="warning"
                title="Scrap a part"
                data-testid={`btn-scrap-part-${s.step_id}`}
                onClick={(e) => { e.stopPropagation(); onScrap(s); }}
              >
                <DeleteSweep />
              </IconButton>
            )}
            {isStaff && (
              <>
                {/* Native title= instead of MUI <Tooltip>: on a 500-row table
                    each MUI Tooltip mounts its own listeners + portal-ready
                    subtree. The text is plain, so the browser tooltip is
                    equivalent and far cheaper per row. */}
                <IconButton
                  size="small"
                  color="error"
                  title="Admin Adjust"
                  data-testid={`btn-admin-${s.step_id}`}
                  onClick={(e) => { e.stopPropagation(); onAdminEdit(s); }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  title="Cancel Work Order"
                  data-testid={`btn-delete-${s.step_id}`}
                  onClick={(e) => { e.stopPropagation(); onDelete(s); }}
                >
                  <Delete />
                </IconButton>
              </>
            )}
            {/* Single action: opens the production-recording dialog.
                Material-aware big button on operation-scoped pages; plain
                Start everywhere else. Mirrors v1 — clicking does not
                transition any state; submission of the dialog does. */}
            {(s.status === 'planned' || s.status === 'in_progress') && (
              materialAware ? (
                <MaterialAwareStartButtonV2 step={s} onStart={onStart} />
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayArrow />}
                  data-testid={`btn-start-${s.step_id}`}
                  onClick={(e) => { e.stopPropagation(); onStart(s); }}
                >
                  Start
                </Button>
              )
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={showDemand ? 10 : 9} sx={{ py: 0 }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Grid container spacing={2}>
                {/* Materials first — what's needed and whether it's in stock
                    is the operator's first question on expanding a row. */}
                <Grid item xs={12}>
                  <MaterialRequirementsTableV2
                    workOrderId={s.work_order_id}
                    step={s}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <Info /> Work Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Operation:</strong>{' '}
                    {s.operation_name || s.operation_code}
                  </Typography>
                  {s.total_steps > 1 && (
                    <>
                      <Typography variant="body2">
                        <strong>Routing Step:</strong> {s.sequence} of {s.total_steps}
                      </Typography>
                      {s.depends_on_sequence && (
                        <Typography variant="body2">
                          <strong>Depends On:</strong> Step {s.depends_on_sequence} ({s.depends_on_status || 'planned'})
                        </Typography>
                      )}
                    </>
                  )}
                  {s.due_date && (
                    <Typography variant="body2">
                      <strong>Due:</strong> {new Date(s.due_date).toLocaleDateString()}
                    </Typography>
                  )}
                  {s.notes && (
                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      <strong>Notes:</strong> {s.notes}
                    </Typography>
                  )}

                  {/* Which customer order this WO exists for — in v2 every
                      WO belongs to exactly one order line, so this is a
                      direct attribution, not a guess. */}
                  <Box
                    data-testid={`wo-order-detail-${s.step_id}`}
                    sx={{
                      mt: 1.5, p: 1.25, borderRadius: 1,
                      border: '1px solid', borderColor: 'primary.light',
                      bgcolor: 'primary.50',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      <ShoppingCart sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      For Customer Order
                    </Typography>
                    {s.customer_order_number ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Tooltip title="View everything going to this order" arrow>
                            <Chip
                              label={s.customer_order_number}
                              size="small"
                              color="primary"
                              data-testid={`order-chip-expanded-${s.step_id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onOrderSummary(s.customer_order_id);
                              }}
                              sx={{ fontWeight: 700, cursor: 'pointer' }}
                            />
                          </Tooltip>
                          <Typography variant="body2" fontWeight="bold">
                            {s.customer_name || 'Unknown customer'}
                          </Typography>
                          {s.customer_order_status && (
                            <Chip
                              label={orderStatusLabel(s.customer_order_status)}
                              size="small"
                              color={orderStatusColor(s.customer_order_status)}
                              variant="outlined"
                            />
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Line {s.line_number}: ordered{' '}
                          {parseFloat(s.line_quantity_ordered) || 0}, shipped{' '}
                          {parseFloat(s.line_quantity_shipped) || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This work order makes {parseFloat(s.quantity_to_produce) || 0}
                          {' '}× {s.output_item_code} for this order only.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No customer order linked.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DemandTimelineDisplayV2 step={s} />
                </Grid>
                {s.customer_order_id != null && (
                  <Grid item xs={12}>
                    <OrderFactoryNotesV2
                      orderId={s.customer_order_id}
                      initialNotes={s.customer_order_notes}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

// ---------------------------------------------------------------------------
// Consolidated group row — memoized. Same 9-column table, one row per group.
// Aggregation rules are unchanged from the previous inline implementation
// (see git history of ShopFloorTerminalV2.jsx for the full commentary).
// ---------------------------------------------------------------------------

export const GroupRowV2 = React.memo(function GroupRowV2({
  g, sources, isExpanded, expansionId, groupByDate, materialAware, isStaff,
  showDemand, highlightCfg,
  onToggle, onStart, onAdminEdit, onDelete, onOrderSummary, onGroupStart, onScrap,
}) {
  const itemHighlights = highlightsForItem(
    highlightCfg, g.output_item_code, g.output_item_name,
  );
  // Aggregates over the source step records.
  const totalToProduce = sources.reduce(
    (acc, s) => acc + (parseFloat(s.quantity_to_produce) || 0), 0,
  );
  const totalCompleted = sources.reduce(
    (acc, s) => acc + (parseFloat(s.quantity_completed) || 0), 0,
  );
  const needNow = sources.reduce((acc, s) => {
    const total = parseFloat(s.quantity_to_produce) || 0;
    const done = parseFloat(s.quantity_completed) || 0;
    return acc + Math.max(0, total - done);
  }, 0);

  // One entry per distinct customer order, with the group quantity that
  // belongs to it — so the row says exactly who the units are for.
  const orderInfos = [];
  {
    const seen = new Map();
    for (const s of sources) {
      if (!s.customer_order_number) continue;
      let info = seen.get(s.customer_order_number);
      if (!info) {
        info = {
          number: s.customer_order_number,
          orderId: s.customer_order_id,
          customerName: s.customer_name || '',
          notes: s.customer_order_notes || '',
          qty: 0,
        };
        seen.set(s.customer_order_number, info);
        orderInfos.push(info);
      }
      info.qty += parseFloat(s.quantity_to_produce) || 0;
    }
  }
  // Distinct root products this group feeds into — labelled by NAME
  // (operators think in product names; the code lives in the tooltip).
  const distinctRoots = [];
  {
    const seenRoots = new Set();
    for (const s of sources) {
      if (!s.root_output_item_code || seenRoots.has(s.root_output_item_code)) continue;
      seenRoots.add(s.root_output_item_code);
      distinctRoots.push({
        code: s.root_output_item_code,
        name: s.root_output_item_name || s.root_output_item_code,
      });
    }
  }
  const progress = totalToProduce > 0
    ? Math.min(100, Math.round((totalCompleted / totalToProduce) * 100))
    : 0;

  // Urgency: pick the most-urgent across sources.
  const urgencyRank = { OVERDUE: 0, URGENT: 1, HIGH: 2, NORMAL: 3 };
  const urgency = sources.reduce((acc, s) => {
    const u = getUrgencyLevel(s);
    return urgencyRank[u] < urgencyRank[acc] ? u : acc;
  }, 'NORMAL');

  const sourceCount = sources.length;
  const firstSource = sources[0];

  // Due-date column content.
  const moreDates = !groupByDate && g.dates && g.dates.length > 1
    ? `+${g.dates.length - 1} more date${g.dates.length - 1 === 1 ? '' : 's'}`
    : null;
  const dueDateCell = (!g.due_date && (!g.dates || g.dates.length === 0))
    ? <Typography variant="body2" color="text.secondary">-</Typography>
    : <DueDateCell dueDate={g.due_date} moreDates={moreDates} />;

  // Customer-order column content: one clickable chip per order, each with
  // the quantity of this group that belongs to it. Clicking opens the
  // order drill-in; "+N more" expands the row to the full mini-row list.
  let customerCell;
  if (orderInfos.length === 0) {
    customerCell = <Typography variant="body2" color="text.secondary">-</Typography>;
  } else {
    const MAX_SHOWN = 4;
    customerCell = (
      <Box sx={{ ...SKIP_CELL_SX, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {orderInfos.slice(0, MAX_SHOWN).map((o) => (
          <Box key={o.number} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title={`${o.customerName || 'View order'} — click for full order detail`} arrow>
              <Chip
                label={o.number}
                size="small"
                color="primary"
                data-testid={`group-order-chip-${g.key}-${o.number}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOrderSummary(o.orderId);
                }}
                sx={{ fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              />
            </Tooltip>
            <Typography variant="body2" fontWeight="bold">
              × {o.qty}
            </Typography>
          </Box>
        ))}
        {orderInfos.length > MAX_SHOWN && (
          <Chip
            label={`+${orderInfos.length - MAX_SHOWN} more — expand`}
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); onToggle(expansionId); }}
            sx={{ cursor: 'pointer' }}
          />
        )}
        {orderInfos.length === 1 && firstSource?.line_number != null && (
          <Typography variant="caption" color="text.secondary">
            Line {firstSource.line_number}
          </Typography>
        )}
      </Box>
    );
  }

  // Material status: route through MaterialStatusChipV2 on the first source.
  const materialStatusCell = !materialAware
    ? <Chip label={g.operation_code || '-'} size="small" color="default" />
    : firstSource
      ? <MaterialStatusChipV2 step={firstSource} />
      : <Chip label="-" size="small" />;

  // WO numbers shown in the left column so a specific work order can be
  // found without expanding the group (deduped — a multi-step WO can
  // contribute more than one source step).
  const woNumbers = [...new Set(sources.map((src) => src.wo_number))].filter(Boolean);

  return (
    <>
      <TableRow
        data-testid={`group-row-${g.key}`}
        sx={{
          '&:hover': { backgroundColor: 'action.hover' },
          cursor: 'pointer',
        }}
        onClick={() => onToggle(expansionId)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip
                  label={`${sourceCount} WO${sourceCount === 1 ? '' : 's'}`}
                  size="small"
                  color="info"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              {woNumbers.slice(0, 3).map((woNumber) => (
                <Typography
                  key={woNumber}
                  variant="caption"
                  sx={{ display: 'block', fontWeight: 700, lineHeight: 1.4 }}
                >
                  {woNumber}
                </Typography>
              ))}
              {woNumbers.length > 3 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  +{woNumbers.length - 3} more
                </Typography>
              )}
              {/* Multi-step routing: say WHICH step this group row is, and
                  when its previous step hasn't recorded production, say so —
                  otherwise a blocked step 2 row is indistinguishable from
                  step 1. (Single-source groups only; multi-source groups
                  show per-WO step chips in the expansion.) */}
              {sourceCount === 1 && firstSource && (
                <Box sx={{ my: 0.5 }}>
                  <StepIndicator step={firstSource} />
                </Box>
              )}
              <Chip
                label={urgency}
                color={getPriorityColor(urgency)}
                size="small"
                icon={urgency === 'OVERDUE' ? <ErrorIcon /> : undefined}
                sx={{ mt: 0.5 }}
              />
              {sourceCount === 1 && firstSource && (
                <DependencyBlockerAlert step={firstSource} />
              )}
            </Box>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        </TableCell>

        <TableCell>
          {/* Name first — operators think in product names, not codes. */}
          <Box sx={SKIP_CELL_SX}>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {applyHighlight(g.output_item_name, itemHighlights)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {applyHighlight(g.output_item_code, itemHighlights)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              via {g.operation_code}{g.operation_name ? ` — ${g.operation_name}` : ''}
            </Typography>
            {distinctRoots.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  For:
                </Typography>
                {distinctRoots.slice(0, 5).map((root) => (
                  <Tooltip key={root.code} title={root.code} arrow>
                    <Chip
                      data-testid={`group-root-chip-${g.key}-${root.code}`}
                      label={root.name}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Tooltip>
                ))}
                {distinctRoots.length > 5 && (
                  <Chip
                    label={`+${distinctRoots.length - 5}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            )}
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant="h5" color="error.main" fontWeight="bold">
            {needNow}
          </Typography>
          <Typography variant="caption">of {totalToProduce} total</Typography>
        </TableCell>

        {showDemand && (
          <TableCell>
            {/* Every source shares the same output_item_id, so the first
                source's demand_timeline is correct for the whole group.
                Optional column (default hidden) via the Demand toggle. The
                inner Box fixes the width — maxWidth on a TableCell is
                ignored by auto table layout. */}
            <Box sx={{ ...SKIP_CELL_SX, width: 150 }}>
              <DemandTimelineCellV2 timeline={firstSource?.demand_timeline} />
            </Box>
          </TableCell>
        )}

        <TableCell>{materialStatusCell}</TableCell>

        <TableCell>{customerCell}</TableCell>

        {/* height:1px lets the inner 100% chain resolve, so the notes boxes
            stretch to the row's full height (browser table-cell quirk). */}
        <TableCell sx={{ height: '1px' }}>
          {/* Order Notes column — one editable note per distinct order in
              the group (autosaves; live-synced with every other copy). */}
          {orderInfos.length === 0 ? (
            <Typography variant="body2" color="text.secondary">-</Typography>
          ) : (
            <Box
              sx={{
                ...SKIP_CELL_SX,
                width: 210, height: '100%',
                display: 'flex', flexDirection: 'column', gap: 1,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {orderInfos.slice(0, 3).map((o) => (
                <Box
                  key={o.number}
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  {orderInfos.length > 1 && (
                    <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
                      {o.number}
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <OrderFactoryNotesV2
                      orderId={o.orderId}
                      initialNotes={o.notes}
                      compact
                    />
                  </Box>
                </Box>
              ))}
              {orderInfos.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  +{orderInfos.length - 3} more — expand the row
                </Typography>
              )}
            </Box>
          )}
        </TableCell>

        <TableCell>{dueDateCell}</TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 100, mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                color={progress >= 100 ? 'success' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold">{progress}%</Typography>
          </Box>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Start = record production across the whole group in one go.
                Expanding to per-order mini-rows stays on the row click.
                Both group shapes get a material-aware CAN MAKE button
                (green = full, orange = partial, red = shortage) so the
                operator sees it without expanding: single-source groups
                check that WO directly; multi-source groups go through the
                group-aware endpoint (every member tree's pot counts). */}
            {materialAware && firstSource ? (
              sourceCount === 1 ? (
                <MaterialAwareStartButtonV2
                  step={firstSource}
                  testId={`btn-group-start-${g.key}`}
                  onStart={() => onGroupStart(
                    sources, `${g.output_item_name} (${g.output_item_code})`,
                  )}
                />
              ) : (
                <GroupMaterialAwareStartButtonV2
                  step={firstSource}
                  workOrderIds={sources.map((src) => src.work_order_id)}
                  remaining={needNow}
                  testId={`btn-group-start-${g.key}`}
                  onStart={() => onGroupStart(
                    sources, `${g.output_item_name} (${g.output_item_code})`,
                  )}
                />
              )
            ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrow />}
              data-testid={`btn-group-start-${g.key}`}
              onClick={(e) => {
                e.stopPropagation();
                onGroupStart(sources, `${g.output_item_name} (${g.output_item_code})`);
              }}
            >
              Start
            </Button>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={showDemand ? 10 : 9} sx={{ py: 0 }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
              {/* What goes INTO this item and whether it's in stock — the
                  v1-style requirements table, scaled to the whole group's
                  remaining quantity. Sub-assemblies appear here too, so
                  prerequisite stock is visible at a glance. Rendered above
                  the per-order source list: materials are the operator's
                  first question on expanding a row. */}
              {firstSource && (
                <Box sx={{ mb: 2 }} data-testid={`group-materials-${g.key}`}>
                  <MaterialRequirementsTableV2
                    workOrderId={firstSource.work_order_id}
                    step={firstSource}
                    remainingOverride={needNow}
                    workOrderIds={sources.map((src) => src.work_order_id)}
                  />
                </Box>
              )}
              <Typography variant="subtitle2" gutterBottom>
                Source Work Orders ({sourceCount})
              </Typography>
              <Stack spacing={1} data-testid={`group-sources-${g.key}`}>
                {sources.map((s) => {
                  const total = parseFloat(s.quantity_to_produce) || 0;
                  const done = parseFloat(s.quantity_completed) || 0;
                  const remaining = Math.max(0, total - done);
                  return (
                    <Paper
                      key={s.step_id}
                      variant="outlined"
                      data-testid={`group-source-${g.key}-${s.step_id}`}
                      sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {s.wo_number}
                          </Typography>
                          {s.total_steps > 1 && (
                            <Chip
                              label={`Step ${s.sequence}/${s.total_steps}`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                          {/* Which order this source WO belongs to — the
                              whole point of expanding a combined row. */}
                          {s.customer_order_number && (
                            <>
                              <Tooltip title="View everything going to this order" arrow>
                                <Chip
                                  label={s.customer_order_number}
                                  size="small"
                                  color="primary"
                                  data-testid={`order-chip-${s.step_id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOrderSummary(s.customer_order_id);
                                  }}
                                  sx={{ fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
                                />
                              </Tooltip>
                              <Typography variant="body2" fontWeight="bold">
                                {s.customer_name || ''}
                              </Typography>
                              {s.customer_order_status && (
                                <Chip
                                  label={orderStatusLabel(s.customer_order_status)}
                                  size="small"
                                  color={orderStatusColor(s.customer_order_status)}
                                  variant="outlined"
                                />
                              )}
                            </>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {s.customer_order_number
                            ? `line ${s.line_number} · `
                            : ''}
                          qty {total} · remaining {remaining} · {s.status}
                          {s.due_date ? ` · due ${new Date(s.due_date).toLocaleDateString()}` : ''}
                        </Typography>
                        {/* What these units end up in — only shown for
                            sub-assemblies (top-level WOs ARE the product). */}
                        {s.root_output_item_name
                          && s.root_output_item_id !== s.output_item_id && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            goes into:{' '}
                            <strong>{s.root_output_item_name}</strong>
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              {' '}({s.root_output_item_code})
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {onScrap && (s.status === 'planned' || s.status === 'in_progress') && (
                          <Tooltip title="Scrap a part" arrow>
                            <IconButton
                              size="small"
                              color="warning"
                              data-testid={`btn-scrap-part-${s.step_id}`}
                              onClick={() => onScrap(s)}
                            >
                              <DeleteSweep />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isStaff && (
                          <>
                            <Tooltip title="Admin Adjust" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                data-testid={`btn-admin-${s.step_id}`}
                                onClick={() => onAdminEdit(s)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Work Order" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                data-testid={`btn-delete-${s.step_id}`}
                                onClick={() => onDelete(s)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {/* Order number right next to Start so the operator
                            can see exactly which order they're signing to. */}
                        {s.customer_order_number
                          && (s.status === 'planned' || s.status === 'in_progress') && (
                          <Chip
                            label={s.customer_order_number}
                            size="small"
                            color="primary"
                            data-testid={`order-chip-action-${s.step_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOrderSummary(s.customer_order_id);
                            }}
                            sx={{ fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
                          />
                        )}
                        {(s.status === 'planned' || s.status === 'in_progress') && (
                          materialAware ? (
                            <MaterialAwareStartButtonV2 step={s} onStart={onStart} />
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PlayArrow />}
                              data-testid={`btn-start-${s.step_id}`}
                              onClick={() => onStart(s)}
                            >
                              Start
                            </Button>
                          )
                        )}
                      </Box>
                      {/* Shared per-ORDER factory notes, editable here too. */}
                      {s.customer_order_id != null && (
                        <Box sx={{ width: '100%', mt: 1 }}>
                          <OrderFactoryNotesV2
                            orderId={s.customer_order_id}
                            initialNotes={s.customer_order_notes}
                          />
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});
